(() => {
  const MOBILE_QUERY = '(max-width: 1024px)';

  const ensureScrollTriggerManager = () => {
    const manager = window.SpeedianceScrollTrigger || {};

    manager._triggerIds = manager._triggerIds || new WeakMap();
    manager._triggerIdIndex = manager._triggerIdIndex || 0;
    manager.getTriggerId =
      manager.getTriggerId ||
      ((root, prefix) => {
        if (!root) return `${prefix}-scroll`;
        if (!manager._triggerIds.has(root)) {
          manager._triggerIdIndex += 1;
          const rootId = root.id ? root.id.replace(/[^a-zA-Z0-9_-]/g, '-') : manager._triggerIdIndex;
          manager._triggerIds.set(root, `${prefix}-${rootId}-${manager._triggerIdIndex}`);
        }
        return manager._triggerIds.get(root);
      });
    manager.getRefreshPriority =
      manager.getRefreshPriority ||
      ((root, offset = 0) => {
        const sections = Array.from(document.querySelectorAll('[data-gm3-motors], [data-gmu-highlights]'));
        const index = Math.max(0, sections.indexOf(root));
        return index * 10 + offset;
      });
    manager.requestRefresh =
      manager.requestRefresh ||
      ((delay = 0) => {
        if (!window.ScrollTrigger) return;

        window.clearTimeout(manager._refreshTimer);
        const run = () => {
          if (manager._refreshRaf) return;
          manager._refreshRaf = window.requestAnimationFrame(() => {
            manager._refreshRaf = null;
            window.ScrollTrigger.sort?.();
            window.ScrollTrigger.refresh();
          });
        };

        if (delay > 0) {
          manager._refreshTimer = window.setTimeout(run, delay);
        } else {
          run();
        }
      });

    window.SpeedianceScrollTrigger = manager;
    return manager;
  };

  const setVideoButtonState = (button, playing) => {
    if (!button) return;
    const playIcon = button.querySelector('.gm3-motors__play-icon--play');
    const pauseIcon = button.querySelector('.gm3-motors__play-icon--pause');
    if (playIcon) playIcon.hidden = playing;
    if (pauseIcon) pauseIcon.hidden = !playing;
    button.setAttribute('aria-pressed', playing ? 'true' : 'false');
    button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  };

  const initMotors = (root) => {
    if (root.dataset.gm3MotorsReady === 'true') return;
    root.dataset.gm3MotorsReady = 'true';

    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const stage = root.querySelector('[data-gm3-motors-stage]');
    const mediaCard = root.querySelector('[data-gm3-motors-media-card]');
    const heading = root.querySelector('[data-gm3-motors-heading]');
    const shadow = root.querySelector('[data-gm3-motors-shadow]');
    const video = root.querySelector('video');
    const toggle = root.querySelector('[data-gm3-motors-video-toggle]');
    const autoplayEnabled = root.dataset.gm3MotorsAutoplay === 'true';
    const scrollManager = ensureScrollTriggerManager();

    let introTrigger = null;
    let pinTimeline = null;
    let observer = null;

    if (video) {
      video.controls = false;
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('disableremoteplayback', '');
      video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
      video.setAttribute('x-webkit-airplay', 'deny');
    }

    const updateButton = () => setVideoButtonState(toggle, Boolean(video && !video.paused));

    const getNavOffset = () => {
      const nanoNav =
        document.querySelector('.nano-hero__nav.is-fixed.is-stuck') ||
        document.querySelector('.nano-hero__nav.is-fixed') ||
        document.querySelector('.nano-hero__nav');
      const navRect = nanoNav?.getBoundingClientRect();
      if (navRect?.height) return Math.round(navRect.height);

      const styles = window.getComputedStyle(document.documentElement);
      const headerHeight = Number.parseFloat(styles.getPropertyValue('--header-height')) || 0;
      const headerOffset = Number.parseFloat(styles.getPropertyValue('--header-offset')) || 0;
      return Math.round(headerHeight + headerOffset || 72);
    };

    const playVideo = () => {
      if (!video || !autoplayEnabled) return;
      video.muted = true;
      video.playsInline = true;
      video.play().then(updateButton).catch(updateButton);
    };

    const pauseVideo = () => {
      if (!video) return;
      video.pause();
      updateButton();
    };

    const teardownDesktop = () => {
      introTrigger?.kill();
      introTrigger = null;
      pinTimeline?.scrollTrigger?.kill();
      pinTimeline?.kill();
      pinTimeline = null;

      resetAnimatedState();
    };

    const resetAnimatedState = () => {
      if (window.gsap) {
        window.gsap.set([mediaCard, heading, shadow].filter(Boolean), { clearProps: 'all' });
      } else {
        [mediaCard, heading, shadow].filter(Boolean).forEach((element) => element.removeAttribute('style'));
      }
      root.style.removeProperty('--gm3-motors-nav-offset');
    };

    const disableDesktop = () => {
      introTrigger?.disable(true);
      pinTimeline?.scrollTrigger?.disable(true);
      resetAnimatedState();
    };

    const enableDesktop = () => {
      if (!introTrigger || !pinTimeline?.scrollTrigger) {
        initDesktop();
        return;
      }

      root.style.setProperty('--gm3-motors-nav-offset', `${getNavOffset()}px`);
      introTrigger.enable();
      pinTimeline.scrollTrigger.enable();
      scrollManager.requestRefresh();
    };

    const initDesktop = () => {
      if (mediaQuery.matches || !window.gsap || !window.ScrollTrigger || !stage || !mediaCard) return;

      window.gsap.registerPlugin(window.ScrollTrigger);
      const navOffset = getNavOffset();
      root.style.setProperty('--gm3-motors-nav-offset', `${navOffset}px`);

      window.gsap.set(mediaCard, {
        scale: 0.7,
        y: '24vh',
        opacity: 0,
      });
      if (heading) {
        window.gsap.set(heading, {
          y: () => Math.max(window.innerHeight * 0.36, 260),
          opacity: 0,
        });
      }
      if (shadow) window.gsap.set(shadow, { opacity: 0 });

      introTrigger = window.ScrollTrigger.create({
        id: `${scrollManager.getTriggerId(root, 'gm3-motors')}-intro`,
        trigger: root,
        start: 'top bottom',
        end: () => `top top+=${getNavOffset() + 96}`,
        scrub: true,
        fastScrollEnd: true,
        invalidateOnRefresh: true,
        refreshPriority: scrollManager.getRefreshPriority(root),
        animation: window.gsap.to(mediaCard, {
          scale: 1,
          y: 0,
          opacity: 1,
          ease: 'none',
        }),
      });

      pinTimeline = window.gsap.timeline({
        scrollTrigger: {
          id: `${scrollManager.getTriggerId(root, 'gm3-motors')}-pin`,
          trigger: stage,
          start: () => `top top+=${getNavOffset()}`,
          end: '+=125%',
          pin: stage,
          scrub: true,
          anticipatePin: 1,
          fastScrollEnd: true,
          invalidateOnRefresh: true,
          refreshPriority: scrollManager.getRefreshPriority(root, 1),
          onRefresh: () => {
            root.style.setProperty('--gm3-motors-nav-offset', `${getNavOffset()}px`);
          },
          onEnter: playVideo,
          onEnterBack: playVideo,
          onLeaveBack: pauseVideo
        },
      });

      if (heading) {
        pinTimeline.to(heading, {
          y: 0,
          opacity: 1,
          ease: 'none',
          duration: 0.58,
        });
      }

      if (shadow) {
        pinTimeline.to(
          shadow,
          {
            opacity: 1,
            ease: 'none',
            duration: 0.22,
          },
          '+=0.08'
        );
      }

      scrollManager.requestRefresh();
    };

    const syncMode = () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      if (mediaQuery.matches) {
        disableDesktop();
        scrollManager.requestRefresh();
        if (video && autoplayEnabled) {
          observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) playVideo();
                else pauseVideo();
              });
            },
            { threshold: 0.35 }
          );
          observer.observe(root);
        }
      } else {
        enableDesktop();
      }

      updateButton();
    };

    toggle?.addEventListener('click', () => {
      if (!video) return;
      if (video.paused) {
        video.muted = true;
        video.playsInline = true;
        video.play().then(updateButton).catch(updateButton);
      } else {
        video.pause();
        updateButton();
      }
    });

    video?.addEventListener('play', updateButton);
    video?.addEventListener('pause', updateButton);
    video?.addEventListener('ended', updateButton);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncMode);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(syncMode);
    }

    root._gm3MotorsCleanup = () => {
      teardownDesktop();
      observer?.disconnect();
      observer = null;
      pauseVideo();
      scrollManager.requestRefresh();
    };

    syncMode();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-gm3-motors]').forEach(initMotors);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-gm3-motors]').forEach((root) => {
      root._gm3MotorsCleanup?.();
      delete root.dataset.gm3MotorsReady;
    });
  });
})();
